import { type ReactNode } from 'react';
import { usePermissions, type ClinicMgmtPermission } from '../context/usePermissions';

interface RequirePermissionProps {
  /** Single permission required. */
  permission?: ClinicMgmtPermission;
  /** User must hold at least one of these permissions. */
  anyOf?: ClinicMgmtPermission[];
  /** User must hold all of these permissions. */
  allOf?: ClinicMgmtPermission[];
  /** Rendered when the permission check fails. Defaults to null (renders nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Inline UI guard that renders children only if the current user passes the
 * permission check. SysAdmin always passes. Use for action buttons, table
 * row actions, or any per-affordance gating.
 *
 * Examples:
 *   <RequirePermission permission="UserCanEditClinicProfile">
 *     <button>Edit clinic</button>
 *   </RequirePermission>
 *
 *   <RequirePermission anyOf={['UserCanReviewClinicVerification', 'UserCanCompleteClinicVerification']}>
 *     <VerificationActions />
 *   </RequirePermission>
 */
export default function RequirePermission({
  permission,
  anyOf,
  allOf,
  fallback = null,
  children,
}: Readonly<RequirePermissionProps>) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let allowed = true;
  if (permission) allowed = allowed && hasPermission(permission);
  if (anyOf?.length) allowed = allowed && hasAnyPermission(...anyOf);
  if (allOf?.length) allowed = allowed && hasAllPermissions(...allOf);

  return allowed ? <>{children}</> : <>{fallback}</>;
}
